/**
 * Pure helpers for the Avatar component's fallback (initials) rendering. Kept
 * DOM-free so they are trivially unit-testable and reused across apps.
 */

/**
 * Derive up to two uppercase initials from a display name or email identity.
 *
 *  - "Ada Lovelace"        → "AL"
 *  - "ada@example.com"     → "A"  (local part before @, first letter)
 *  - "ada.lovelace@x.com"  → "AL" (split the local part on . / _ / -)
 *  - ""                    → "?"  (generic fallback)
 */
export function initialsFor(nameOrEmail: string | null | undefined): string {
  const raw = (nameOrEmail ?? "").trim();
  if (raw.length === 0) return "?";

  // For an email, use the local part and split on common separators.
  const local = raw.includes("@") ? raw.split("@")[0] : raw;
  const parts = local
    .split(/[\s._-]+/)
    .map((p) => p.trim())
    .filter((p) => p.length > 0);

  if (parts.length === 0) return "?";
  if (parts.length === 1) {
    return parts[0].slice(0, 1).toUpperCase();
  }
  return (parts[0].slice(0, 1) + parts[parts.length - 1].slice(0, 1)).toUpperCase();
}

/**
 * Deterministic hue [0,360) from an identity string, so the same student keeps
 * the same fallback color across renders and across the two apps.
 */
export function hueFor(nameOrEmail: string | null | undefined): number {
  const s = nameOrEmail ?? "";
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h * 31 + s.charCodeAt(i)) % 360;
  }
  return h;
}
